import { useState, useEffect } from 'react'
import connectApi from '../api/connectApi'
import { constants } from 'helpers'

export const offlineTime = () => {
    return new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const WORKING = 'working'

const checkUserNetworkStatus = async (maxPingTime) => {
    try {
        const response = await Promise.race([
            connectApi.get('/ping-pong/'),
            new Promise((resolve, reject) =>
                setTimeout(
                    () => reject(new Error(constants.TIME_OUT)),
                    maxPingTime
                )
            )
        ])
        if (response?.status !== 200) {
            throw new Error(constants.OFFLINE)
        }
        return {
            isOnline: true,
            isSlowNetwork: false,
            isValidCSPong: response.data?.checks?.CoreServices === WORKING
        }
    } catch (error) {
        return {
            isOnline: false,
            isSlowNetwork: error.message === constants.TIME_OUT,
            isValidCSPong: false
        }
    }
}

export const useNetworkConnection = () => {
    const [online, setOnline] = useState({
        isOnline: navigator.onLine,
        lastOnline: '',
        isSlowNetwork: false,
        isValidCSPong: true
    })

    const handleInternetConnection = () => {
        if (!navigator.onLine) {
            setOnline({
                isOnline: false,
                lastOnline: offlineTime(),
                isSlowNetwork: false,
                isValidCSPong: false
            })
        } else {
            setOnline((prev) => ({
                ...prev,
                isOnline: true,
                isSlowNetwork: false,
                lastOnline: '',
                isValidCSPong: true
            }))
            checkNetworkStatus()
        }
    }

    const checkNetworkStatus = async () => {
        const { isOnline, isSlowNetwork, isValidCSPong } =
            await checkUserNetworkStatus(
                process.env.REACT_APP_MAX_PING_TIME || 5000
            )
        setOnline({
            isOnline,
            lastOnline: isOnline ? '' : offlineTime(),
            isSlowNetwork,
            isValidCSPong
        })
    }

    useEffect(() => {
        window.addEventListener('online', handleInternetConnection)
        window.addEventListener('offline', handleInternetConnection)
        const intervalID = setInterval(
            checkNetworkStatus,
            process.env.REACT_APP_PING_INTERVAL_TIME || 60000
        )
        checkNetworkStatus()

        if ('connection' in navigator) {
            const latencyDuration =
                process.env.REACT_APP_MAX_LATENCY_TIME || 300
            const connection = navigator.connection
            const updateConnection = () => {
                setOnline((prev) => ({
                    ...prev,
                    isSlowNetwork: connection.rtt > latencyDuration,
                    latency: connection.rtt
                }))
            }
            connection.addEventListener('change', updateConnection)
            updateConnection()

            return () => {
                clearInterval(intervalID)
                window.removeEventListener('online', handleInternetConnection)
                window.removeEventListener('offline', handleInternetConnection)
                connection.removeEventListener('change', updateConnection)
            }
        }
        return () => {
            clearInterval(intervalID)
            window.removeEventListener('online', handleInternetConnection)
            window.removeEventListener('offline', handleInternetConnection)
        }
    }, [])

    return { ...online }
}
